import { FormField } from '@/components/ui/form';
import useSymbol from '@/hooks/use-symbol';
import { useMarketStore } from '@/store/use-markets-store';
import { OrderType } from '@/types';
import { bigIntToDecimalStr, parseDecimalToBigInt } from '@/utils/value-format';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getLimitPriceFromMaxSlippage,
  getSlippageReferencePrice,
} from '../../utils/math';
import { getDefaultMaxSlippageValue } from '../max-slippage-input';
import { OrderFormSchema } from '../order-form';
import { Tabs, TabsList, TabsTrigger } from '../order-tabs';

type Props = {
  onChange?: (value: string) => void;
};

export default function OrderTypeTabs({ onChange }: Props) {
  const symbol = useSymbol();
  const { t } = useTranslation();
  const form = useFormContext<OrderFormSchema>();

  return (
    <FormField
      control={form.control}
      name="order.orderType"
      render={({ field }) => (
        <Tabs
          defaultValue={OrderType.MARKET}
          className="w-full"
          value={field.value}
          onValueChange={(value) => {
            onChange?.(value);

            field.onChange(value as OrderType);

            const { marketData, marketSpec } =
              useMarketStore(symbol).getState();

            const limitPrice = bigIntToDecimalStr(
              marketData.midPrice,
              marketSpec.priceDecimals,
            );

            if (value === OrderType.LIMIT) {
              form.setValue('order.limitPrice', limitPrice);
              form.setValue('order.timeInForce', 'GTC');
              form.setValue('tp.limitPrice', '');
              form.setValue('sl.limitPrice', '');
            } else if (value === OrderType.SCALE) {
              form.setValue('order.limitPrice', limitPrice);
              form.setValue('startPrice', limitPrice);
              form.setValue('endPrice', limitPrice);
              form.setValue('ordersCount', '');
              form.setValue('sizeSkew', '1.0');
              form.setValue('order.timeInForce', 'GTC');
              form.setValue('tp.limitPrice', '');
              form.setValue('sl.limitPrice', '');
            } else {
              const { isBuy } = form.getValues().order;

              const limitPriceWithSlippage = getLimitPriceFromMaxSlippage(
                isBuy,
                parseDecimalToBigInt(
                  getDefaultMaxSlippageValue(symbol).toString(),
                  2n,
                ),
                getSlippageReferencePrice(symbol, isBuy),
                marketSpec.priceDecimals,
              ).decimal;

              form.setValue('order.limitPrice', limitPriceWithSlippage);
              form.setValue('order.timeInForce', undefined);
            }
          }}
        >
          <TabsList className="grid grid-cols-3">
            <TabsTrigger
              value={OrderType.MARKET}
              className="overflow-hidden border-b border-border hover:bg-vestgrey-800 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              {t('orderForm.market')}
            </TabsTrigger>
            <TabsTrigger
              id="limit-order-tab"
              value={OrderType.LIMIT}
              className="overflow-hidden border-b border-border hover:bg-vestgrey-800 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              {t('orderForm.limit')}
            </TabsTrigger>
            <TabsTrigger
              id="scale-order-tab"
              value={OrderType.SCALE}
              className="overflow-hidden border-b border-border hover:bg-vestgrey-800 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              {t('orderForm.scale')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
    />
  );
}
